import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Lock, ShieldCheck, Zap } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { RecaptchaConfig } from '../api/client';
import { Alert } from '../components/Feedback';
import { useAuth } from '../components/AuthProvider';
import { Logo } from '../components/Logo';
import { RecaptchaWidget } from '../components/RecaptchaWidget';

const highlights = [
  { icon: Lock, text: 'Credentials encrypted at rest, never returned by any endpoint' },
  { icon: ShieldCheck, text: 'Strict per-user isolation: one account can never see another’s data' },
  { icon: Zap, text: 'Read-only SQL with timeouts and row limits enforced on every query' },
];

/** Combined sign-in and sign-up screen; `mode` selects which. */
export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = useState<RecaptchaConfig | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaConfigFailed, setRecaptchaConfigFailed] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const handleCaptchaToken = useCallback((token: string) => setCaptchaToken(token), []);
  const handleCaptchaError = useCallback((message: string) => setCaptchaError(message), []);

  useEffect(() => {
    api.auth
      .recaptchaConfig()
      .then(setRecaptchaConfig)
      .catch(() => {
        setRecaptchaConfigFailed(true);
        setCaptchaError('Verification settings could not be loaded. Refresh and try again.');
      })
      .finally(() => setRecaptchaReady(true));
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const isRegister = mode === 'register';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setCaptchaError('');
    if (!recaptchaReady) return;
    if (recaptchaConfig?.enabled && !captchaToken) {
      setError('Complete the reCAPTCHA challenge to continue.');
      return;
    }
    setBusy(true);
    try {
      if (isRegister) await signUp(email, name, password, captchaToken);
      else await signIn(email, password, captchaToken);
      navigate('/dashboard', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
    } finally {
      setBusy(false);
      if (recaptchaConfig?.enabled) {
        setCaptchaToken('');
        setCaptchaResetKey((key) => key + 1);
      }
    }
  };

  return (
    <div className="auth">
      <section className="auth-intro">
        <Logo className="logo-auth-intro" />
        <h1>
          Your databases,
          <br />
          <span>readable by AI.</span>
        </h1>
        <p className="auth-lede">
          Connect a database, generate an MCP token, and point any MCP client at it. The agent
          inspects your schema and runs read-only SQL — scoped to your account and nothing else.
        </p>
        <ul className="auth-highlights">
          {highlights.map(({ icon: Icon, text }) => (
            <li key={text}>
              <Icon size={17} />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="auth-panel">
        <form className="auth-card" onSubmit={submit}>
          <Logo className="logo-auth" />
          <h2>{isRegister ? 'Create your account' : 'Welcome back'}</h2>
          <p className="muted">
            {isRegister
              ? 'Register to connect a database and generate an MCP token.'
              : 'Sign in to manage your databases and MCP tokens.'}
          </p>

          {(error || captchaError) && <Alert tone="error">{error || captchaError}</Alert>}

          {isRegister && (
            <label>
              Name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
                autoComplete="name"
              />
            </label>
          )}

          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={isRegister ? 8 : undefined}
              maxLength={72}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
            {isRegister && (
              <small className="muted">
                At least 8 characters, up to 72. Stored as a bcrypt hash.
              </small>
            )}
          </label>

          {recaptchaConfig?.enabled && recaptchaConfig.site_key && (
            <RecaptchaWidget
              key={captchaResetKey}
              siteKey={recaptchaConfig.site_key}
              onToken={handleCaptchaToken}
              onError={handleCaptchaError}
            />
          )}

          <button
            className="primary full"
            disabled={busy || !recaptchaReady || recaptchaConfigFailed || Boolean(recaptchaConfig?.enabled && !captchaToken)}
          >
            {busy
              ? 'Please wait…'
              : !recaptchaReady
                ? 'Loading…'
                : isRegister
                  ? 'Create account'
                  : 'Sign in'}
          </button>

          <p className="auth-switch">
            {isRegister ? (
              <>
                Already have an account? <Link to="/login">Sign in</Link>
              </>
            ) : (
              <>
                No account yet? <Link to="/register">Register</Link>
              </>
            )}
          </p>
        </form>
      </section>
    </div>
  );
}
